import React, { useMemo } from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { naturalSort } from "@shared/utils/ArrayUtils";
import { FloorHeatMap } from '@features/reporting/services/OfficeHeatMapService';
import { binStyle, formatPercent, heatBin } from "./heatScale";
import { areaRatio, deskRatio, describe, roomRatio, tooltipContent } from "./floorHeat";
import { HeatTooltip, HeatTooltipContent, useHeatTooltip } from "./HeatTooltip";

interface Tile {
  key: string;
  label: string;
  ratio?: number;
  description: string;
  tooltip: HeatTooltipContent;
}

// For floors without a floor plan: every desk, area and room as a coloured tile
export function DeskHeatTiles({floor}: { floor: FloorHeatMap }) {
  const {t} = useTranslation();
  const {tip, markProps} = useHeatTooltip();

  const groups = useMemo(() => {
    const desks: Tile[] = (naturalSort([...floor.desks], 'label') as FloorHeatMap['desks']).map(desk => {
      const ratio = deskRatio(desk, floor.daysInRange);
      const detail = `${desk.usage} of ${floor.daysInRange} days booked`;
      return {key: `desk-${desk.deskId}`, label: desk.label, ratio,
        description: describe(desk.label, ratio, detail), tooltip: tooltipContent(desk.label, ratio, detail)};
    });
    const areas: Tile[] = floor.areas.map(area => {
      const ratio = areaRatio(area, floor.daysInRange);
      return {key: `area-${area.areaId}`, label: area.label, ratio,
        description: describe(area.label, ratio), tooltip: tooltipContent(area.label, ratio)};
    });
    const rooms: Tile[] = floor.meetingRooms.map(room => {
      const ratio = roomRatio(room);
      const detail = `${room.totalHoursBooked.toFixed(1)} hours booked`;
      return {key: `room-${room.roomId}`, label: room.name, ratio,
        description: describe(room.name, ratio, detail), tooltip: tooltipContent(room.name, ratio, detail)};
    });
    return [
      {title: 'reports-page.office-usage.heat-map.desks', tiles: desks},
      {title: 'reports-page.office-usage.heat-map.areas', tiles: areas},
      {title: 'reports-page.office-usage.heat-map.meeting-rooms', tiles: rooms},
    ].filter(group => group.tiles.length > 0);
  }, [floor]);

  if (groups.length === 0) {
    return <BodyRegular>{t('reports-page.office-usage.heat-map.nothing-on-floor')}</BodyRegular>;
  }

  return (
    <>
      {groups.map(group => (
        <Group key={group.title}>
          <BodyRegular weight={600} style={{marginBottom: 8}}>{t(group.title)}</BodyRegular>
          <Tiles>
            {group.tiles.map(tile => {
              const bin = heatBin(tile.ratio);
              return (
                <TileBox key={tile.key} {...markProps(tile.description, tile.tooltip)} style={binStyle(bin)}>
                  <TileLabel>{tile.label}</TileLabel>
                  {/* The hatching already says there is no capacity */}
                  {tile.ratio !== undefined && <span>{formatPercent(tile.ratio)}</span>}
                </TileBox>
              );
            })}
          </Tiles>
        </Group>
      ))}
      <HeatTooltip tip={tip} />
    </>
  );
}

const Group = styled.div`
  margin-bottom: 20px;
`

const Tiles = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
`

const TileBox = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 56px;
  padding: 6px 8px;
  border-radius: 6px;
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  &:focus-visible {
    outline: 2px solid ${Colours.black};
    outline-offset: 1px;
  }
`

const TileLabel = styled.span`
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
